import { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';

import FetchModule from '../../Share/Network/FetchModule.js';
import { useLanguage } from '../../../Context/LanguageContext';

import AccountInput from '../../../Component/AccountComponent/AccountInput.js';

const SearchPassword = () => {
  const { t } = useLanguage();

  const [ownerClickCount, setOwnerClickCount] = useState(0);
  const [emailClickCount, setEmailClickCount] = useState(0);
  const [idClickCount, setIdClickCount] = useState(0);
  const [isInvalidEmail, setIsInvalidEmail] = useState(true);
  const [sendExplanation, setSendExplanation] = useState(t('findAccount.emailSentPw'));
  const [planationText, setPlanationText] = useState(t('findAccount.requiredField'));

  const OwnerRef = useRef();
  const EmailRef = useRef();
  const IdRef = useRef();

  const OwnerPlanationRef = useRef();
  const EmailPlanationRef = useRef();
  const IdPlanationRef = useRef();
  const TotalPlanationRef = useRef();

  // 이메일 유효성 상태 변경시 안내 텍스트 갱신 //
  useEffect(() => {
    if (isInvalidEmail) setPlanationText(t('findAccount.requiredField'));
    else setPlanationText(t('findAccount.invalidEmail'));
  }, [isInvalidEmail])

  // Input 클릭 카운트 변경시 유효성 표시 갱신 //
  useEffect(() => {
    if (ownerClickCount === 0 && emailClickCount === 0 && idClickCount === 0) return;

    if (OwnerRef.current.getValue() === '' && ownerClickCount > 0) OwnerPlanationRef.current.style.opacity = 1;
    else OwnerPlanationRef.current.style.opacity = 0;

    if (EmailRef.current.getValue() === '' && emailClickCount > 0) EmailPlanationRef.current.style.opacity = 1;
    else EmailPlanationRef.current.style.opacity = 0;

    if (IdRef.current.getValue() === '' && idClickCount > 0) IdPlanationRef.current.style.opacity = 1;
    else IdPlanationRef.current.style.opacity = 0;
  }, [ownerClickCount, emailClickCount, idClickCount])

  // 이메일 형식 확인 정규식 //
  function isEmail(asValue) {
    const regExp = /^[0-9a-zA-Z]([-_\.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_\.]?[0-9a-zA-Z])*\.[a-zA-Z]{2,6}$/i;

    return regExp.test(asValue);
  }

  // Input 컴포넌트 클릭시 //
  function clickCountFn(className, clickCount) {
    if (className === 'OwnerInput') setOwnerClickCount(clickCount);
    else if (className === 'EmailInput') setEmailClickCount(clickCount);
    else if (className === 'IdInput') setIdClickCount(clickCount);
  }

  // 비밀번호 변경 URL 전송 버튼 클릭시 //
  function onClickSendResetEmail() {
    const ownerName = OwnerRef.current.getValue();
    const email = EmailRef.current.getValue();
    const userId = IdRef.current.getValue();
    let invalidCount = 0;

    if (ownerName === '') {
      OwnerRef.current.plusClickCount();
      OwnerRef.current.setDefaultPlaceholder(t('findAccount.ownerNamePlaceholder'));
      OwnerPlanationRef.current.style.opacity = 1;

      invalidCount += 1;
    }

    if (email === '') {
      EmailRef.current.plusClickCount();
      EmailRef.current.setDefaultPlaceholder(t('findAccount.emailPlaceholder'));
      EmailPlanationRef.current.style.opacity = 1;

      invalidCount += 1;
    }
    else if (!isEmail(email)) {
      setIsInvalidEmail(false);
      EmailRef.current.setDefaultPlaceholder(t('findAccount.emailPlaceholder'));
      EmailPlanationRef.current.style.opacity = 1;

      invalidCount += 1;
    }

    if (userId === '') {
      IdRef.current.plusClickCount();
      IdRef.current.setDefaultPlaceholder(t('findAccount.idPlaceholder'));
      IdPlanationRef.current.style.opacity = 1;
    }

    if (invalidCount > 0) return;

    const requestData = {
      ownerName: ownerName,
      email: email,
      userId: userId
    }

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postData('identifier/send', 'POST', requestData);

        if (responseData.status === 200) setSendExplanation(t('findAccount.emailSentToAddress'));
        else setSendExplanation(t('findAccount.invalidInfo'));
      }
    )();

    TotalPlanationRef.current.style.opacity = 1;
  }

  return (
    <TopLevelWrapper>
      <InfoBox>
        <InfoWrapper>
          <Title>{t('findAccount.findPw')}</Title>
          <InputInfo>{t('findAccount.ownerName')}</InputInfo>
          <AccountInput
            ref={OwnerRef}
            className='OwnerInput'
            placeholder={t('findAccount.ownerNamePlaceholder')}
            type='text'
            clickCountFn={clickCountFn} />
          <AccountExplanation ref={OwnerPlanationRef}>{t('findAccount.requiredField')}</AccountExplanation>
          <InputInfo>{t('findAccount.email')}</InputInfo>
          <AccountInput
            ref={EmailRef}
            className='EmailInput'
            placeholder={t('findAccount.emailPlaceholder')}
            type='text'
            clickCountFn={clickCountFn} />
          <AccountExplanation ref={EmailPlanationRef}>{planationText}</AccountExplanation>
          <InputInfo>{t('findAccount.id')}</InputInfo>
          <AccountInput
            ref={IdRef}
            className='IdInput'
            placeholder={t('findAccount.idPlaceholder')}
            type='text'
            clickCountFn={clickCountFn} />
          <AccountExplanation ref={IdPlanationRef}>{t('findAccount.requiredField')}</AccountExplanation>
          <TotalExplanationWrapper>
            <TotalExplanation ref={TotalPlanationRef}>{sendExplanation}</TotalExplanation>
          </TotalExplanationWrapper>
          <SendingButton onClick={onClickSendResetEmail}>
            <SendingButtonText>{t('findAccount.sendPwResetEmail')}</SendingButtonText>
          </SendingButton>
        </InfoWrapper>
      </InfoBox>
    </TopLevelWrapper>
  )
}

export default SearchPassword;

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoBox = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;

  width: 582px;
  height: 595px;

  border-radius: 50px;
  background-color: #FFFFFF;
  box-shadow: 0px 40px 50px rgba(240, 232, 227, 0.5);

  transition: all 0.3s ease-in-out;
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 468px;
  height: 100%;
  padding: 50px 0;
`;

const Title = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 24px;

  color: #000000;

  margin-top: 0px;
  margin-right: 0px;
  margin-bottom: 38px;
  margin-left: 0px;

  transition: all 0.3s ease-in-out;
`;

const InputInfo = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 16px;

  color: #000000;

  margin: 19px 0 0 0;

  transition: all 0.3s ease-in-out;
`;

const AccountExplanation = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: #FF3131;

  margin: 4px 0 0 0;
  opacity: 0;

  transition: all 0.3s ease-in-out;
`;

const SendingButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 468px;
  height: 46px;

  margin-top: 0;

  background-color: #FF8B48;
  border-radius: 23px;

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
    background-color: #FF7322;
  }
`;

const SendingButtonText = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 16px;

  color: #FFFFFF;
  margin: 10px 0 10px 0;

  &:hover {
    cursor: pointer;
  }
`;

const TotalExplanationWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  flex: 1;
`;

const TotalExplanation = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 12px;

  color: #FF8B48;

  margin: 0;

  opacity: 0;
  transition: all 0.3s ease-in-out;
`;
